import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConversationBubble from './ConversationBubble';
import VoiceConfirmCard, { ConfirmCardData } from './VoiceConfirmCard';
import VoiceQuickPicks, { VoiceQuickPickOption } from './VoiceQuickPicks';
import HistoryModal from './HistoryModal';
import Icon from './ui/Icon';
import { useOverlayFocusTrap } from '../hooks/useOverlayFocusTrap';
import { requestMicrophoneAccess } from '../utils/mobileAudioCapture';
import type { AssistantSuggestedQueryPill } from '../constants/assistantSuggestedQueries';
import type { VoiceMessage, VoiceSession } from '../utils/voiceSessionStorage';
import VoiceStatusAnnouncer from './voiceAssistant/VoiceStatusAnnouncer';
import { useNearBottomScroll } from '../hooks/useNearBottomScroll';
import { useVoiceUiPhase } from '../hooks/useVoiceUiPhase';
import './GlobalVoiceAssistantUI.css';

interface GlobalVoiceAssistantUIProps {
  messages: VoiceMessage[];
  isProcessing: boolean;
  isRecording: boolean;
  activeComponent?: string | null;
  componentData?: any;
  showHistoryModal: boolean;
  pastSessions: VoiceSession[];
  selectedPastSession?: VoiceSession | null;
  isMinimized: boolean;
  currentTranscript?: string;
  confirmCard?: ConfirmCardData | null;
  quickPicks?: VoiceQuickPickOption[];
  dynamicSuggestedQueries?: string[];
  pageSuggestedQueries?: AssistantSuggestedQueryPill[];
  onQuickPickSelect?: (option: VoiceQuickPickOption) => void;

  onClose: () => void;
  onMinimize?: () => void;
  onMaximize?: () => void;
  onStartVoiceInput: (prewarmedStream?: MediaStream | null) => void;
  onStopVoiceInput?: (mode?: 'done' | 'cancel') => void;
  onTranscription: (text: string) => void;
  onProcessingChange: (isProcessing: boolean) => void;
  onOpenHistory?: (show?: boolean) => void;
  onCloseHistoryModal?: () => void;
  onSelectPastSession?: (session: VoiceSession) => void;
  onLoadSession?: (session: VoiceSession) => void;
  onLoadSelectedSession?: () => void;
  onStartNewChat?: () => void;
  onClearHistory?: () => void;
  onCloseComponent?: () => void;
  onConfirmTransaction?: () => void;
  onConfirmQuotation?: () => void;

  assistantContext?: any;
  company?: any;
  notify?: (message: string, type?: string) => void;
}

const GlobalVoiceAssistantUI: React.FC<GlobalVoiceAssistantUIProps> = ({
  messages,
  isProcessing,
  isRecording,
  showHistoryModal,
  pastSessions,
  selectedPastSession,
  isMinimized,
  currentTranscript,
  confirmCard,
  quickPicks = [],
  pageSuggestedQueries = [],
  onQuickPickSelect,
  onClose,
  onMinimize,
  onMaximize,
  onStartVoiceInput,
  onStopVoiceInput,
  onTranscription,
  onOpenHistory,
  onCloseHistoryModal,
  onSelectPastSession,
  onLoadSession,
  onLoadSelectedSession,
  onStartNewChat,
  onClearHistory,
  notify,
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [textMode, setTextMode] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [waveLevels, setWaveLevels] = useState<number[]>([]);
  const { phase, silenceSecondsRemaining, alertMessage } = useVoiceUiPhase({
    isRecording,
    isProcessing,
    confirmCard,
  });
  const { showJumpToLatest, jumpToLatest, onScroll } = useNearBottomScroll(chatScrollRef, {
    contentKey: `${messages.length}:${currentTranscript || ''}:${isProcessing}:${Boolean(confirmCard)}:${phase}`,
  });

  useOverlayFocusTrap(overlayRef, !isMinimized);

  const hasChatContent = messages.length > 0 || Boolean(currentTranscript?.trim()) || isProcessing;
  const suggestionPills = pageSuggestedQueries;

  const liveStatusLabel = (() => {
    if (phase === 'waiting-for-finish' && silenceSecondsRemaining != null) {
      return t('voice.status.silenceCountdown', 'Sending in {{seconds}}s…', {
        seconds: silenceSecondsRemaining,
      });
    }
    if (phase === 'speech-detected') return t('voice.status.speechDetected', 'Speech detected');
    if (phase === 'requesting-permission') {
      return t('voice.status.requestingPermission', 'Requesting microphone permission');
    }
    if (phase === 'finishing') return t('voice.status.finishing', 'Finishing capture');
    return t('voice.status.listening', 'Listening');
  })();

  const handleSuggestedQuery = (query: string) => {
    if (isProcessing || isRecording) return;
    onTranscription(query);
  };

  useEffect(() => {
    const handleWaveform = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setWaveLevels(Array.isArray(detail?.levels) ? detail.levels : []);
    };
    window.addEventListener('voiceWaveform', handleWaveform as EventListener);
    return () => window.removeEventListener('voiceWaveform', handleWaveform as EventListener);
  }, []);

  const handleHistoryClose = () => {
    onCloseHistoryModal?.() ?? onOpenHistory?.(false);
  };

  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = textInput.trim();
    if (!trimmed || isProcessing) return;
    setTextInput('');
    setTextMode(false);
    onTranscription(trimmed);
  };

  const threadMessages = messages;
  const micLabel = isRecording
    ? t('voice.modal.stopRecording', 'Stop recording')
    : t('voice.modal.startVoice', 'Start recording');

  if (isMinimized) {
    return (
      <button
        type="button"
        className="gva-fab"
        onClick={onMaximize}
        title={t('voice.aiAssistant', 'Open voice assistant')}
        aria-label={t('voice.aiAssistant', 'Open voice assistant')}
      >
        AI
      </button>
    );
  }

  return (
    <div ref={overlayRef} className={`gva-overlay${textMode ? ' gva-overlay--text-mode' : ''}`}>
      <VoiceStatusAnnouncer
        phase={phase}
        silenceSecondsRemaining={silenceSecondsRemaining}
        alertMessage={alertMessage}
      />

      <header className="gva-header">
        <div className="gva-header-brand">
          <div className="gva-header-logo">
            <Icon name="app-logo" size={24} />
          </div>
          <h1 className="gva-header-title">EZAccounting.ai</h1>
        </div>
        <div className="gva-header-actions">
          <button
            type="button"
            className="gva-icon-btn"
            onClick={() => onOpenHistory?.(true)}
            title={t('voice.modal.chatHistory', 'Chat history')}
            aria-label={t('voice.modal.chatHistory', 'Chat history')}
          >
            <Icon name="chat" size={20} />
          </button>
          {onMinimize && (
            <button
              type="button"
              className="gva-icon-btn"
              onClick={onMinimize}
              title={t('dashboard.assistant.collapse', 'Minimize')}
              aria-label={t('dashboard.assistant.collapse', 'Minimize')}
            >
              <Icon name="minimize" size={20} />
            </button>
          )}
          <button
            type="button"
            className="gva-icon-btn"
            onClick={onClose}
            title={t('voice.close', 'Close')}
            aria-label={t('voice.close', 'Close')}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
      </header>

      <main className={`gva-main${hasChatContent ? ' gva-main--active-chat' : ''}`}>
        <div className="gva-main-top">
          <div className={`gva-orb-container${hasChatContent ? ' gva-orb-container--compact' : ''}`}>
            <div className="gva-orb-ring-outer" />
            <div className="gva-orb-ring-inner" />
            <div className={`gva-orb-core ${isRecording ? 'recording' : ''}`}>
              <Icon name={isRecording ? 'mic' : 'app-logo'} size={hasChatContent ? (isRecording ? 32 : 36) : (isRecording ? 48 : 56)} />
            </div>
          </div>

          {isRecording && (
            <div className="gva-waveform" aria-hidden="true">
              {(waveLevels.length ? waveLevels : new Array(28).fill(0)).map((level, i) => (
                <span
                  key={i}
                  className="gva-waveform-bar"
                  style={{ height: `${Math.max(8, Math.min(100, level * 100))}%` }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="gva-chat-scroll" ref={chatScrollRef} onScroll={onScroll}>
          {threadMessages.length === 0 && !isRecording && !isProcessing && (
            <p className="gva-chat-empty">{t('voice.emptyHint', 'Tap the mic and speak, or type a message below.')}</p>
          )}

          {threadMessages.map((msg) => (
            <ConversationBubble key={msg.id} message={msg} dark />
          ))}

          {isRecording && (
            <div className="gva-bubble-row gva-bubble-row-user gva-live-user-row">
              <div className="gva-bubble-col gva-bubble-col-user">
                <p className="gva-bubble-label">{t('voice.you', 'You')}</p>
                <div className="gva-bubble gva-bubble-user gva-bubble-dark gva-bubble-live">
                  <p>{currentTranscript?.trim() || liveStatusLabel}</p>
                </div>
                <span className="gva-bubble-time gva-bubble-time-live">{liveStatusLabel}</span>
              </div>
            </div>
          )}

          {isProcessing && (
            <div
              className="gva-processing"
              aria-label={
                phase === 'transcribing'
                  ? t('voice.status.transcribing', 'Transcribing')
                  : t('voice.status.understanding', 'Understanding your request')
              }
            >
              <div className="gva-processing-dot" />
              <div className="gva-processing-dot" />
              <div className="gva-processing-dot" />
            </div>
          )}

          {confirmCard && <VoiceConfirmCard data={confirmCard} variant="inline" />}
        </div>

        {showJumpToLatest && (
          <button type="button" className="gva-jump-latest" onClick={jumpToLatest}>
            {t('voice.jumpToLatest', 'Jump to latest')}
          </button>
        )}
      </main>

      {quickPicks.length > 0 && onQuickPickSelect && (
        <VoiceQuickPicks
          options={quickPicks}
          onSelect={onQuickPickSelect}
          disabled={isProcessing}
        />
      )}

      <footer className={`gva-footer${textMode ? ' gva-footer--text-mode' : ''}`}>
        {suggestionPills.length > 0 && (
          <div className="gva-suggestions">
            <p className="gva-suggestions-label">{t('dashboard.assistant.suggestedQueries')}</p>
            <div className="gva-suggestion-pills">
              {suggestionPills.map(({ label, command, dynamic }) => (
                <button
                  key={command}
                  type="button"
                  className={`gva-suggestion-pill${dynamic ? ' gva-suggestion-pill--dynamic' : ''}`}
                  onClick={() => handleSuggestedQuery(command)}
                  disabled={isProcessing || isRecording}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {textMode && (
          <form className="gva-text-input-bar" onSubmit={handleTextSubmit}>
            <input
              className="gva-text-input"
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder={t('voice.typePlaceholder', 'Type your message…')}
              autoFocus
              disabled={isProcessing}
            />
            <button type="submit" className="gva-text-send" disabled={isProcessing || !textInput.trim()}>
              {t('voice.send', 'Send')}
            </button>
          </form>
        )}

        <div className="gva-controls" dir="auto">
          {isRecording && (
            <div className="voice-capture-actions">
              <button type="button" className="voice-done-btn" onClick={() => onStopVoiceInput?.('done')}>
                {t('voice.done', 'Done')}
              </button>
              <button type="button" className="voice-cancel-capture-btn" onClick={() => onStopVoiceInput?.('cancel')}>
                {t('voice.cancelCapture', 'Cancel')}
              </button>
            </div>
          )}

          <button
            type="button"
            className={`gva-control-btn gva-keyboard-btn${textMode ? ' active' : ''}`}
            onClick={() => setTextMode((v) => !v)}
            title={t('voice.keyboardInput', 'Keyboard input')}
            aria-label={t('voice.keyboardInput', 'Keyboard input')}
            aria-pressed={textMode}
          >
            <Icon name="keyboard" size={24} />
            <span className="gva-control-label">{t('voice.typeLabel', 'Type')}</span>
          </button>

          <button
            type="button"
            className={`gva-mic-btn ${isRecording ? 'recording' : ''}`}
            onClick={() => {
              if (isRecording) {
                onStopVoiceInput?.('done');
              } else if (!isProcessing) {
                const micPromise = requestMicrophoneAccess();
                void micPromise.then((stream) => onStartVoiceInput(stream));
              }
            }}
            disabled={isProcessing && !isRecording}
            title={micLabel}
            aria-label={micLabel}
            aria-pressed={isRecording}
          >
            {isRecording && <span className="gva-mic-ring" />}
            <Icon name={isRecording ? 'mic-stop' : 'mic'} size={24} className="gva-mic-icon" />
          </button>

          <button
            type="button"
            className="gva-control-btn"
            onClick={() => document.getElementById('voice-assistant-file-input')?.click()}
            title={t('voice.attachFile', 'Attach file')}
            aria-label={t('voice.attachFile', 'Attach file')}
          >
            <Icon name="attach" size={24} />
          </button>
        </div>

        <input
          type="file"
          id="voice-assistant-file-input"
          accept=".csv,.tsv,.txt,.xlsx,.xls"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              window.dispatchEvent(new CustomEvent('voiceAssistantFileUpload', { detail: { file } }));
            }
            e.target.value = '';
          }}
        />
      </footer>

      {showHistoryModal && (
        <HistoryModal
          isOpen={showHistoryModal}
          onClose={handleHistoryClose}
          pastSessions={pastSessions}
          selectedSession={selectedPastSession}
          onSelectSession={onSelectPastSession || (() => {})}
          onLoadSession={onLoadSession || (() => {})}
          onLoadSelectedSession={onLoadSelectedSession || (() => {})}
          onStartNewChat={onStartNewChat || (() => {})}
          onClearHistory={onClearHistory || (() => {})}
          messages={messages}
          notify={notify}
        />
      )}
    </div>
  );
};

export default GlobalVoiceAssistantUI;
